/**
 * Client for engine /v1/rfqs (estimate workspace).
 */
import { ENGINE_BASE, engineFetch } from "./config";

function baseUrl(): string {
  return (
    (typeof window !== "undefined" && (window as { __ENGINE_URL__?: string }).__ENGINE_URL__) ||
    ENGINE_BASE
  );
}

export class RfqApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "RfqApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface StockShortage {
  part_no?: string;
  stock_item_id?: number | null;
  need?: number;
  available?: number;
  reason?: string;
}

export function shortagesFromError(err: unknown): StockShortage[] {
  if (!(err instanceof RfqApiError)) return [];
  const raw = err.details?.shortages;
  if (!Array.isArray(raw)) return [];
  return raw.filter((row) => row && typeof row === "object") as StockShortage[];
}

export function rfqUserMessage(err: unknown, t: (key: string) => string): string {
  if (err instanceof RfqApiError) {
    if (err.status === 503) return t("erp.err.db");
    if (err.code === "no_intel") return t("rfq.err.noIntel");
    if (err.code === "invalid_job") return t("rfq.err.noJob");
    if (err.code === "invalid_transition") return t("rfq.err.outcome");
    if (err.code === "invalid_lines") return t("rfq.err.intake");
    if (err.code === "triage_not_ready") return err.message || t("rfq.err.triage");
    if (err.code === "audit_blocker") return err.message || t("rfq.err.audit");
    if (err.code === "margin_unapproved") return t("rfq.err.margin");
    if (err.code === "invalid_qtys") return t("rfq.err.invalidQtys");
    if (err.code === "insufficient_stock" || err.code === "stock_not_found") {
      return err.message || t("rfq.err.shortage");
    }
    if (err.code === "quote_locked") return t("rfq.err.quoteLocked");
    if (err.code === "use_mail_send") return t("rfq.err.useMailSend");
  }
  return t("rfq.err.generic");
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await engineFetch(`${baseUrl()}${path}`, init);
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    let code: string | undefined;
    let msg = `rfq ${path} ${res.status}`;
    let details: Record<string, unknown> | undefined;
    if (detail && typeof detail === "object") {
      const d = detail as { detail?: unknown; code?: string; message?: string; shortages?: unknown };
      const inner = (
        d.detail && typeof d.detail === "object"
          ? d.detail
          : d
      ) as {
        code?: string;
        message?: string;
        shortages?: unknown;
        details?: Record<string, unknown>;
        field?: unknown;
      };
      if (typeof d.detail === "string") {
        msg = d.detail;
      } else {
        code = inner.code;
        msg = inner.message || msg;
        if (inner.details && typeof inner.details === "object") details = inner.details;
        // Validation errors name the offending input, e.g. `route_json[0].setup_hrs`.
        if (typeof inner.field === "string" && inner.field) {
          details = { ...(details || {}), field: inner.field };
        }
        if (Array.isArray(inner.shortages)) {
          details = { ...(details || {}), shortages: inner.shortages };
        }
      }
    }
    throw new RfqApiError(res.status, msg, code, details);
  }
  return res.json() as Promise<T>;
}

export type RfqStatus =
  | "draft"
  | "estimating"
  | "waiting_customer"
  | "ready"
  | "sent"
  | "cancelled"
  | "won"
  | "lost";
export type OutcomeReason = "price" | "lead" | "capacity" | "no_bid";
export type LineKind = "part" | "bom";
export type SourceKind = "catalog_part" | "catalog_bom" | "upload_job" | "manual" | "intake";
export type IntakeKind = "cad" | "spreadsheet" | "text" | "pdf" | "image";
export type IntakeReadState = "pending" | "read" | "unread" | "failed";
export type MakeBuy = "undecided" | "make" | "buy" | "outsource" | "mixed";
export type DecisionStatus = "pending" | "decided" | "skipped";

export type PricingMode = "hourly" | "fixed" | "per_area";
export type RateSource = "default" | "template" | "org" | "machines";

/** Physical drivers an op may carry; the engine derives hours from them. */
export interface RouteOpParams {
  material?: string;
  thickness_mm?: number;
  cut_length_mm?: number;
  pierces?: number;
  cut_speed_mm_min?: number;
  pierce_sec?: number;
  holes?: number;
  sec_per_hole?: number;
  weld_length_mm?: number;
  weld_speed_mm_min?: number;
  bends?: number;
  sec_per_bend?: number;
  setup_min?: number;
  cycle_min?: number;
  area_m2?: number;
  cost_try_per_m2?: number;
  min_batch_try?: number;
  [key: string]: number | string | undefined;
}

export interface RouteOp {
  op: string;
  label_tr?: string | null;
  setup_hrs?: number;
  run_hrs_per_unit?: number;
  machine?: string | null;
  est_hours?: number;
  /** Inputs the review card edits. */
  pricing_mode?: PricingMode;
  params?: RouteOpParams;
  /** True once a human typed the hours directly; params stop driving them. */
  overridden?: boolean;
  /** Defaults the engine filled in (table cut speed, default pierce time…). */
  derived?: Record<string, number>;
  fixed_cost_try?: number;
  unit_cost_try?: number;
  min_batch_try?: number;
  note?: string;
  /** Outputs the engine stamps when it prices the line. */
  rate_try_per_hr?: number;
  rate_key?: string;
  rate_source?: RateSource | string;
  setup_cost_try?: number;
  run_cost_try?: number;
  unit_total_try?: number;
  min_batch_applied?: boolean;
  cost_try?: number;
}

export type MaterialSource = "intel" | "stock" | "market" | "manual" | "supplier";

export interface LineMaterial {
  mass_kg?: number;
  try_per_kg?: number;
  /** Explicit per-unit purchase/material cost; wins over mass × price. */
  unit_cost_try?: number;
  source?: MaterialSource | string;
  material_class?: string;
  note?: string;
}

export interface LineCostBreakdown {
  qty?: number;
  ops_cost_try?: number;
  material_cost_try?: number;
  overhead_pct?: number;
  overhead_try?: number;
  total_try?: number;
  hours?: number;
  rate_sources?: string[];
  /** Set when someone typed a line total. Repricing keeps it until the route or qty changes. */
  manual_total_try?: number | null;
}

export interface RfqLine {
  id: string;
  rfq_id?: string | null;
  line_no: number;
  parent_line_id?: string | null;
  kind: LineKind;
  source_kind: SourceKind;
  part_no?: string | null;
  description?: string | null;
  material?: string | null;
  qty: number;
  unit?: string;
  thumbnail_url?: string | null;
  source_job_id?: string | null;
  stock_item_id?: number | null;
  make_buy: MakeBuy;
  /**
   * Engine main does not store this. `suggested_make_buy()` in
   * `rfq/logic.py` is computed from the route and is not on the read model.
   * Present only when a payload actually carries the key.
   */
  suggested_make_buy?: MakeBuy | null;
  process_code?: string | null;
  route_json: RouteOp[];
  material_json?: LineMaterial;
  material_cost_try?: number | null;
  cost_json?: LineCostBreakdown;
  est_hours?: number | null;
  est_lead_days?: number | null;
  est_cost_try?: number | null;
  decision_status: DecisionStatus;
  intel_seeded?: boolean;
  source_item_no?: number | null;
  children?: RfqLine[];
  supplier_ask?: SupplierAsk | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export type SupplierAskStatus = "draft" | "waiting" | "answered";

export interface SupplierAsk {
  id?: string | null;
  rfq_id?: string | null;
  rfq_line_id?: string | null;
  supplier?: string | null;
  last_unit_price_try?: number | null;
  last_lead_days?: number | null;
  status: SupplierAskStatus;
  question_subject?: string | null;
  question_body?: string | null;
  /** Optional. Engine main omits this on the supplier-ask read model. */
  supplier_email?: string | null;
  /** Optional. When the ask was sent. Engine main omits this on the read model. */
  sent_at?: string | null;
  reply_body?: string | null;
  reply_unit_price_try?: number | null;
  reply_lead_days?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  answered_at?: string | null;
}

/** True when a JSON payload included `key`, including an explicit null. */
export function engineReturned(record: object | null | undefined, key: string): boolean {
  return !!record && Object.prototype.hasOwnProperty.call(record, key);
}

/**
 * Supplier-ask contact fields the payload actually carried.
 * A missing key is omitted. A present null stays null. Nothing is invented.
 */
export function readSupplierAskFields(
  ask: SupplierAsk | null | undefined,
): { email?: string | null; sentAt?: string | null } | null {
  if (!ask) return null;
  const out: { email?: string | null; sentAt?: string | null } = {};
  if (engineReturned(ask, "supplier_email")) {
    const raw = ask.supplier_email;
    out.email = typeof raw === "string" && raw.trim() ? raw.trim() : null;
  }
  if (engineReturned(ask, "sent_at")) {
    const raw = ask.sent_at;
    out.sentAt = typeof raw === "string" && raw.trim() ? raw.trim() : null;
  }
  return "email" in out || "sentAt" in out ? out : null;
}

export interface IntakeFinding {
  code: string;
  kind?: "missing" | "uncertain" | "unread" | "failed";
  severity?: "blocker" | "warning";
  field?: string | null;
  message: string;
  source_id?: string | null;
  line_id?: string | null;
}

export interface CustomerReply {
  id?: string | null;
  rfq_id?: string | null;
  body: string;
  created_by_user_id?: string | null;
  created_at?: string | null;
}

export interface IntakeSource {
  id: string;
  rfq_id?: string | null;
  kind: IntakeKind;
  read_state: IntakeReadState;
  reason?: string | null;
  original_name?: string | null;
  upload_id?: string | null;
  source_job_id?: string | null;
  line_ids?: string[];
}

export interface OutsideProcess {
  code: string;
  label: string;
  found: boolean;
  enabled: boolean;
  note?: string | null;
  part_count?: number;
}

export interface RfqRecord {
  id: string;
  rfq_no: string;
  title?: string | null;
  notes?: string | null;
  status: RfqStatus;
  outcome_reason?: OutcomeReason | null;
  outcome_at?: string | null;
  est_hours?: number | null;
  est_lead_days?: number | null;
  est_cost_try?: number | null;
  customer_name?: string | null;
  customer_email?: string | null;
  due_at?: string | null;
  quote_currency?: string | null;
  target_price?: number | null;
  outside_processes?: OutsideProcess[];
  line_count: number;
  pending_count: number;
  decided_count: number;
  /** Demand parts (kind=part). The list badge uses this when the header has no lines. */
  part_count?: number;
  stock_missing_count?: number;
  /** Up to five artifact URLs, in line order. Empty slots are omitted. */
  part_thumbs?: string[];
  lines: RfqLine[];
  produced?: boolean;
  sources?: IntakeSource[];
  findings?: IntakeFinding[];
  waiting_at?: string | null;
  sent_at?: string | null;
  suggested_margin_pct?: number | null;
  approved_margin_pct?: number | null;
  margin_reason?: string | null;
  quote_total_try?: number | null;
  customer_replies?: CustomerReply[];
  created_at?: string | null;
  updated_at?: string | null;
  intel_provider?: string | null;
  intel_confidence?: number | null;
  seeded_line_ids?: string[];
}

export interface RfqProcessTemplate {
  id: string;
  code: string;
  label_tr: string;
  make_buy: MakeBuy;
  lead_days_default: number;
  route_json: RouteOp[];
  sort_order: number;
  /** Shipped default: it can be reset, never deleted. */
  seeded?: boolean;
}

/** The decisions a library template may carry (lines may also be undecided/mixed). */
export type ProcessTemplateMakeBuy = "make" | "buy" | "outsource";

/**
 * One op in a template write. Numbers are sent as typed; the engine validates
 * and answers 422 with `detail.field` instead of clamping.
 */
export interface RfqProcessOpWrite {
  op: string;
  label_tr?: string;
  setup_hrs: number | string | null;
  run_hrs_per_unit: number | string | null;
  machine?: string | null;
  machine_id?: string | null;
  params?: RouteOpParams;
  pricing_mode?: PricingMode;
  fixed_cost_try?: number;
  unit_cost_try?: number;
  min_batch_try?: number;
  note?: string;
}

/** PUT /v1/rfq-processes/{code} body (full replace). */
export interface RfqProcessTemplateWrite {
  code?: string;
  label_tr: string;
  make_buy: ProcessTemplateMakeBuy | string;
  lead_days_default: number | string | null;
  sort_order?: number;
  route_json: RfqProcessOpWrite[];
}

export interface RfqStats {
  won: number;
  lost: number;
  cancelled: number;
  open: number;
  win_rate: number | null;
}

export function fetchRfqs(limit = 50): Promise<{ items: RfqRecord[]; count: number }> {
  return jsonFetch(`/v1/rfqs?limit=${limit}`);
}

export function fetchRfq(id: string): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}`);
}

export function fetchRfqStats(): Promise<RfqStats> {
  return jsonFetch("/v1/rfqs/stats");
}

export function postRfqOutcome(
  id: string,
  body: { outcome: "won" | "lost"; reason?: OutcomeReason | null },
): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/outcome`, {
    method: "POST",
    body: JSON.stringify({
      outcome: body.outcome,
      reason: body.reason ?? null,
    }),
  });
}

export function createRfq(body: {
  title?: string;
  notes?: string;
  customer_name?: string;
  due_at?: string | null;
}): Promise<RfqRecord> {
  return jsonFetch("/v1/rfqs", { method: "POST", body: JSON.stringify(body) });
}

export function updateRfq(
  id: string,
  body: {
    title?: string;
    notes?: string;
    status?: RfqStatus;
    customer_name?: string;
    customer_email?: string;
    due_at?: string | null;
    approved_margin_pct?: number | null;
    quote_currency?: string;
    target_price?: number | null;
    outside_processes?: { code: string; enabled: boolean }[];
  },
): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteRfq(id: string): Promise<{ deleted: boolean }> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function addRfqLine(rfqId: string, body: Record<string, unknown>): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(rfqId)}/lines`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function attachRfqUpload(
  rfqId: string,
  body: { job_id?: string; upload_ids?: string[]; master_upload_id?: string },
): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(rfqId)}/uploads`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function submitRfqIntake(
  rfqId: string,
  body: { upload_ids?: string[]; mail_body?: string },
): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(rfqId)}/intake`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateRfqLine(
  rfqId: string,
  lineId: string,
  body: Record<string, unknown>,
): Promise<RfqRecord> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function deleteRfqLine(rfqId: string, lineId: string): Promise<RfqRecord> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}`,
    { method: "DELETE" },
  );
}

export function applyRfqProcess(
  rfqId: string,
  lineId: string,
  code: string,
  opts?: { apply?: SeedIntelApply },
): Promise<RfqRecord> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}/apply-process`,
    { method: "POST", body: JSON.stringify({ code, apply: opts?.apply || "merge" }) },
  );
}

export function routeSignature(route: RouteOp[] | null | undefined): string {
  return JSON.stringify(
    (route || []).map((op) => [
      String(op.op || "").trim().toUpperCase(),
      Number(op.setup_hrs || 0),
      Number(op.run_hrs_per_unit || 0),
      String(op.machine || "").trim(),
    ]),
  );
}

export function processOpsAlreadyOnRoute(route: RouteOp[] | null | undefined, incoming: RouteOp[] | null | undefined): boolean {
  const have = new Set(
    (route || []).map((op) => String(op.op || "").trim().toUpperCase()).filter(Boolean),
  );
  const need = (incoming || [])
    .map((op) => String(op.op || "").trim().toUpperCase())
    .filter(Boolean);
  return need.length > 0 && need.every((code) => have.has(code));
}

export type SeedIntelMode = "copy" | "refresh";
export type SeedIntelApply = "overwrite" | "merge";

export function seedRfqIntel(
  rfqId: string,
  lineId: string,
  body?: { mode?: SeedIntelMode; apply?: SeedIntelApply; scope?: "line" | "children" },
): Promise<RfqRecord> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}/seed-intel`,
    { method: "POST", body: JSON.stringify(body || {}) },
  );
}

export function fetchRfqProcesses(): Promise<{ items: RfqProcessTemplate[]; count: number }> {
  return jsonFetch("/v1/rfq-processes");
}

function processPath(code: string): string {
  return `/v1/rfq-processes/${encodeURIComponent(code)}`;
}

/** Create (201) or replace (200) one library template. Existing lines keep their route. */
export function putRfqProcess(code: string, body: RfqProcessTemplateWrite): Promise<RfqProcessTemplate> {
  return jsonFetch(processPath(code), { method: "PUT", body: JSON.stringify(body) });
}

/** Delete a custom template. Seeded codes answer 409 `process_seeded`. */
export function deleteRfqProcess(code: string): Promise<{ deleted: boolean; code: string }> {
  return jsonFetch(processPath(code), { method: "DELETE" });
}

/** Restore a seeded template to its shipped default. Custom codes answer 409 `process_not_seeded`. */
export function resetRfqProcess(code: string): Promise<RfqProcessTemplate> {
  return jsonFetch(`${processPath(code)}/reset`, { method: "POST" });
}

export type FindingSeverity = "blocker" | "warning";
export type FindingKind = "missing" | "unread" | "uncertain" | "conflict" | "needs_customer_ok";

export interface RfqFinding {
  code: string;
  severity: FindingSeverity;
  kind: FindingKind;
  message: string;
  line_id?: string | null;
  field?: string | null;
  evidence?: Record<string, unknown>;
}

export interface RfqTriageResult {
  quote_ready: boolean;
  findings: RfqFinding[];
  question_email?: { subject: string; body_tr: string } | null;
  quote_email?: { subject: string; body_tr: string } | null;
}

export interface RfqAuditResult {
  ready_allowed: boolean;
  findings: RfqFinding[];
}

export function postRfqTriage(id: string): Promise<RfqTriageResult> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/triage`, { method: "POST" });
}

export function postRfqAudit(id: string): Promise<RfqAuditResult> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/audit`, { method: "POST" });
}

export function postRfqWait(id: string): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/wait`, { method: "POST" });
}

export function postRfqSend(id: string): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/send`, { method: "POST" });
}

/** Re-run every line through the org's current rates and Costing settings. */
export function postRfqReprice(id: string): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/reprice`, { method: "POST" });
}

export const RFQ_WHATIF_QTYS = [10, 50, 100] as const;

export interface RfqWhatIfOp {
  op: string;
  setup_cost_try: number;
  run_cost_try: number;
  cost_try: number;
  est_hours: number;
}

export interface RfqWhatIfLine {
  line_id: string;
  hours: number;
  cost_try: number;
  ops?: RfqWhatIfOp[];
  ops_cost_try?: number;
  material_cost_try?: number;
  overhead_try?: number;
}

export interface RfqWhatIfScenario {
  qty: number;
  est_hours: number;
  est_cost_try: number;
  lines: RfqWhatIfLine[];
}

export interface RfqWhatIfResult {
  rfq_id: string;
  scenarios: RfqWhatIfScenario[];
}

export function postRfqWhatIf(
  id: string,
  qtys: number[] = [...RFQ_WHATIF_QTYS],
): Promise<RfqWhatIfResult> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/what-if`, {
    method: "POST",
    body: JSON.stringify({ qtys }),
  });
}

export interface RuntimeFlags {
  mail_send_enabled: boolean;
  mail_receive_enabled: boolean;
  erp_sales_enabled: boolean;
  erp_v2_enabled: boolean;
}

export function fetchRuntimeFlags(): Promise<RuntimeFlags> {
  return jsonFetch("/v1/runtime");
}

export interface SalesOrder {
  id: string;
  so_no: string;
  status: string;
  rfq_id?: string | null;
  customer_name?: string | null;
  total_try?: number;
  lines?: Array<Record<string, unknown>>;
}

export function postRfqConvertToOrder(id: string): Promise<SalesOrder> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/convert-to-order`, { method: "POST" });
}

export async function fetchRfqQuotePdf(id: string): Promise<Blob> {
  const res = await engineFetch(`${baseUrl()}/v1/rfqs/${encodeURIComponent(id)}/quote.pdf`);
  if (!res.ok) {
    let code: string | undefined;
    let msg = `rfq quote.pdf ${res.status}`;
    try {
      const detail = await res.json();
      if (detail && typeof detail === "object") {
        const d = detail as { detail?: { code?: string; message?: string }; code?: string; message?: string };
        const inner = d.detail && typeof d.detail === "object" ? d.detail : d;
        code = inner.code;
        msg = inner.message || msg;
      }
    } catch {
      /* binary error body */
    }
    throw new RfqApiError(res.status, msg, code);
  }
  return res.blob();
}

export function postRfqCustomerReply(id: string, body: string): Promise<RfqRecord> {
  return jsonFetch(`/v1/rfqs/${encodeURIComponent(id)}/customer-replies`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export function fetchSupplierAsk(rfqId: string, lineId: string): Promise<SupplierAsk> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}/supplier-ask`,
  );
}

export function postSupplierAsk(
  rfqId: string,
  lineId: string,
  body?: { status?: SupplierAskStatus; supplier?: string | null },
): Promise<RfqRecord> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}/supplier-ask`,
    { method: "POST", body: JSON.stringify(body || { status: "waiting" }) },
  );
}

export function postSupplierReply(
  rfqId: string,
  lineId: string,
  body: { unit_price_try: number; lead_days: number; body?: string },
): Promise<RfqRecord> {
  return jsonFetch(
    `/v1/rfqs/${encodeURIComponent(rfqId)}/lines/${encodeURIComponent(lineId)}/supplier-reply`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export interface SimilarJobItem {
  work_order_id: string;
  wo_no?: string | null;
  part_no?: string | null;
  qty?: number | null;
  ops?: string[];
  closed_at?: string | null;
  actual_hours?: number | null;
  est_hours?: number | null;
  actual_cost_try?: number | null;
  est_cost_try?: number | null;
  rfq_id?: string | null;
  rfq_no?: string | null;
  rfq_title?: string | null;
  rfq_line_id?: string | null;
  customer_name?: string | null;
  won?: boolean | null;
  score?: number;
}

export interface SimilarJobsResult {
  items: SimilarJobItem[];
  summary: {
    count: number;
    avg_actual_hours: number | null;
    avg_est_hours: number | null;
    avg_actual_cost_try?: number | null;
    avg_est_cost_try?: number | null;
  };
}

export function fetchSimilarJobs(opts: {
  jobId?: string | null;
  rfqLineId?: string | null;
  k?: number;
}): Promise<SimilarJobsResult> {
  const q = new URLSearchParams();
  if (opts.jobId) q.set("job_id", opts.jobId);
  if (opts.rfqLineId) q.set("rfq_line_id", opts.rfqLineId);
  if (opts.k != null) q.set("k", String(opts.k));
  return jsonFetch(`/v1/similar-jobs?${q.toString()}`);
}

export function similarJobsCopy(
  result: SimilarJobsResult | null,
  t: (
    key: "rfq.similar" | "rfq.similarEmpty" | "rfq.similarCost" | "rfq.similarEstOnly" | "rfq.similarActualOnly",
    vars?: Record<string, string>,
  ) => string,
  formatCost?: (n: number | null | undefined) => string,
): { empty: boolean; text: string } {
  const n = result?.summary.count ?? 0;
  const actual = result?.summary.avg_actual_hours;
  const est = result?.summary.avg_est_hours;
  if (!result || n === 0 || actual == null) {
    return { empty: true, text: t("rfq.similarEmpty") };
  }
  const fmt = (h: number) => (Number.isInteger(h) ? String(h) : h.toFixed(1));
  let text = t("rfq.similar", {
    n: String(n),
    actual: fmt(actual),
    est: est == null ? "—" : fmt(est),
  });
  const actualCost = result.summary.avg_actual_cost_try;
  const estCost = result.summary.avg_est_cost_try;
  if (formatCost && (actualCost != null || estCost != null)) {
    const costText =
      actualCost != null && estCost != null
        ? t("rfq.similarCost", { actual: formatCost(actualCost), est: formatCost(estCost) })
        : actualCost != null
          ? t("rfq.similarActualOnly", { actual: formatCost(actualCost) })
          : t("rfq.similarEstOnly", { est: formatCost(estCost) });
    text = `${text} · ${costText}`;
  }
  return { empty: false, text };
}
